import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  ChangeDetectionStrategy,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import target from 'lucide-angular/icons/target';
import { MessageService } from 'primeng/api';
import { FileValidationService } from 'src/app/import-pagos-recaudacion/services/file-validation.service';
import { ToastQueueService } from '../../services/toast-queue.service';

export interface FileUploadItem {
  file: File;
  name: string;
  progress: number;
  isComplete: boolean;
  hasError: boolean;
  errorMessage?: string;
  seleccionado?: boolean;
  loteId?: number;
}

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadComponent implements OnInit, OnChanges {
  @Input() multiple = true;
  @Input() acceptedTypes = '.txt';
  @Input() maxFileSize = 10485760; // 10MB por defecto
  @Input() disabled = false;
  @Input() placeholder = 'Arrastre sus archivos aquí';
  @Input() subPlaceholder = 'O puede subir sus archivos';
  @Input() tertiaryPlaceholder = '(Solo se admiten archivos .txt - Máximo 10MB)';
  @Input() showSelectCheckbox = true;
  @Input() porcentajeSubida = 0;
  @Input() listaArchivos: FileUploadItem[] = [];
  @Input() uploadedFiles: FileUploadItem[] = []; // Ahora también es @Input
  @Input() maxFiles = 5;

  @Output() filesSelected = new EventEmitter<File[]>();
  @Output() filesChanged = new EventEmitter<FileUploadItem[]>();
  @Output() fileRemoved = new EventEmitter<FileUploadItem>();
  @Output() verDatosLote = new EventEmitter<number>();

  @Output() checkFileExists = new EventEmitter<{ file: File; hash: string; fileName: string }>();
  @Output() fileValidationCompleted = new EventEmitter<{
    hash: string;
    exists: boolean;
    file: File;
  }>();

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  // Propiedad interna para manejar los archivos
  private _internalFiles: FileUploadItem[] = [];
  private _pendingValidations = new Map<string, File>(); // hash -> file (archivos esperando validación BD)
  isDragOver = false;

  constructor(
    private readonly messageService: MessageService,
    private readonly fileValidationService: FileValidationService,
    private readonly toastQueue: ToastQueueService,
  ) {}

  ngOnInit(): void {
    // Inicializar con archivos proporcionados desde el padre
    this._internalFiles = [...this.uploadedFiles];
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar cambios en uploadedFiles desde el componente padre
    if (changes['uploadedFiles'] && !changes['uploadedFiles'].firstChange) {
      this._internalFiles = [...this.uploadedFiles];
    }
  }

  // Getter para acceder a los archivos internos
  get files(): FileUploadItem[] {
    return this._internalFiles;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.disabled) {
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.disabled) return;

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(Array.from(files));
      console.log('files dropped', Array.from(files));
    }
  }

  onFileInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target.files) {
      this.handleFiles(Array.from(target.files));
      console.log('target.files', Array.from(target.files));
    }
  }

  private async generateFileHashSample(file: File, sampleSize = 1024 * 100): Promise<string> {
    const slice = file.slice(0, Math.min(sampleSize, file.size));
    const arrayBuffer = await slice.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  private async handleFiles(files: File[]): Promise<void> {
    const validFiles: File[] = [];
    const fileHashes = new Map<string, string>(); // hash -> fileName

    // Generar hashes de archivos existentes
    for (const existingFile of this._internalFiles) {
      const hash = await this.generateFileHashSample(existingFile.file);
      fileHashes.set(hash, existingFile.name);
    }

    // Validar cada archivo nuevo
    for (const file of files) {
      // Validaciones básicas (tamaño, tipo)
      if (!this.validateFile(file)) continue;

      if (
        this._internalFiles.find((f) => f.name === file.name) ||
        validFiles.find((f) => f.name === file.name)
      ) {
        this.toastQueue.addWarn(
          'Archivo duplicado',
          `El archivo "${file.name}" ya ha sido agregado.`,
          5000,
        );
        continue;
      }

      const fileHash = await this.generateFileHashSample(file);

      if (fileHashes.has(fileHash)) {
        const duplicateFileName = fileHashes.get(fileHash);
        this.toastQueue.addWarn(
          'Archivo duplicado',
          `El archivo "${file.name}" tiene el mismo contenido que "${duplicateFileName}".`,
          5000,
        );
        continue;
      }

      const structureValidation = await this.fileValidationService.validateFileStructure(
        file,
        this.fileValidationService.getFileFormat('pagos-recaudacion')!,
      );

      if (!structureValidation.isValid) {
        this.toastQueue.addError(
          'Estructura de archivo inválida',
          `Errores encontrados:\n${structureValidation.errors.slice(0, 3).join('\n')}${
            structureValidation.errors.length > 3 ? '\n...' : ''
          }`,
          10000,
        );
        continue;
      }

      const fullFileHash = await this.fileValidationService.generateFileHash(file);

      this._pendingValidations.set(fullFileHash, file);
      fileHashes.set(fileHash, file.name);

      this.checkFileExists.emit({
        file: file,
        hash: fullFileHash,
        fileName: file.name,
      });
    }

    // Si no hay archivos que requieren validación BD, procesar archivos que pasaron todas las validaciones locales
    if (validFiles.length > 0) {
      this.processValidFiles(validFiles);
    }
  }

  // private handleFiles(files: File[]): void {
  //   const validFiles = files.filter((file) => this.validateFile(file));

  //   if (validFiles.length === 0) return;

  //   // Si no es múltiple, reemplazar archivos existentes
  //   if (!this.multiple) {
  //     this._internalFiles = [];
  //   }

  //   const newFileItems: FileUploadItem[] = validFiles.map((file) => ({
  //     file,
  //     name: file.name,
  //     progress: 0,
  //     isComplete: false,
  //     hasError: false,
  //     seleccionado: false, // Inicializar como no seleccionado
  //   }));

  //   this._internalFiles.push(...newFileItems);
  //   this.filesSelected.emit(validFiles);
  //   this.filesChanged.emit(this._internalFiles);

  //   // Simular progreso de carga
  //   // for (const fileItem of newFileItems) {
  //   //   this.simulateUploadProgress(fileItem);
  //   // }
  // }

  private validateFile(file: File): boolean {
    // Validar tamaño

    // Validar tipo si se especifica
    if (this.acceptedTypes !== '*/*') {
      const acceptedTypesArray = this.acceptedTypes.split(',').map((type) => type.trim());
      const isValidType = acceptedTypesArray.some((type) => {
        if (type.startsWith('.')) {
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        }
        const regex = new RegExp(type.replace('*', '.*'));
        return regex.exec(file.type) !== null;
      });

      if (!isValidType) {
        console.warn(`El archivo ${file.name} no tiene un tipo válido`);
        this.toastQueue.addWarn(
          'Tipo de archivo no válido',
          `El archivo ${file.name} no tiene un tipo válido.`,
          5000,
        );
        return false;
      }
    }

    if (file.size > this.maxFileSize) {
      console.warn(`El archivo ${file.name} excede el tamaño máximo permitido`);
      this.toastQueue.addWarn(
        'Archivo demasiado grande',
        `El archivo ${file.name} excede el tamaño máximo permitido de ${this.formatFileSize(
          this.maxFileSize,
        )}.`,
        5000,
      );
      return false;
    }

    return true;
  }

  removeFile(fileItem: FileUploadItem): void {
    const index = this._internalFiles.indexOf(fileItem);
    if (index > -1) {
      // this._internalFiles.splice(index, 1);
      this.fileRemoved.emit(fileItem);
      // this.filesChanged.emit(this._internalFiles);
      // Emitir cambio en archivos seleccionados al remover
      // this.emitSelectedFiles();
    }
  }

  openFileSelector(): void {
    if (this.disabled) return;
    this.fileInput.nativeElement.click();
  }

  clearAllFiles(): void {
    this._internalFiles = [];
    this.filesChanged.emit(this._internalFiles);
    // Emitir cambio en archivos seleccionados al limpiar
    // this.emitSelectedFiles();
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  getFileIcon(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';

    switch (extension) {
      case 'pdf':
        return 'file-text';
      case 'doc':
      case 'docx':
        return 'file-text';
      case 'xls':
      case 'xlsx':
        return 'file-spreadsheet';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'image';
      case 'zip':
      case 'rar':
        return 'archive';
      default:
        return 'file';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  trackByFile(index: number, item: FileUploadItem): string {
    return `${item.name}-${item.file.size}-${item.file.lastModified}`;
  }

  // onCheckboxChange(fileItem: FileUploadItem): void {
  //   console.log('Checkbox changed for:', fileItem.name, 'New value:', fileItem.seleccionado);
  //   // Emitir los archivos seleccionados cuando cambie la selección
  //   this.emitSelectedFiles();
  // }

  // private emitSelectedFiles(): void {
  //   const selectedFiles = this.uploadedFiles.filter((f) => f.seleccionado);
  //   this.selectedFilesChanged.emit(selectedFiles);
  // }

  verLote(loteId: number): void {
    // Lógica para ver los detalles del lote
    console.log('Ver detalles del lote:', loteId);
    this.verDatosLote.emit(loteId);
  }

  public onFileExistenceChecked(hash: string, exists: boolean, existingData?: any): void {
    const file = this._pendingValidations.get(hash);

    if (!file) {
      console.warn(`No se encontró archivo pendiente para hash: ${hash}`);
      return;
    }

    this._pendingValidations.delete(hash);

    if (exists) {
      this.toastQueue.addWarn(
        'Archivo ya procesado',
        `El archivo "${file.name}" ya ha sido procesado anteriormente${
          existingData?.fechaProcesado
            ? ` el ${new Date(existingData.fechaProcesado).toLocaleDateString()}`
            : ''
        }.`,
        8000,
      );
    } else {
      this.processValidFile(file);
    }

    this.fileValidationCompleted.emit({
      hash,
      exists,
      file,
    });

    if (this._pendingValidations.size === 0) {
      console.log('Todas las validaciones de existencia completadas');
    } else {
      console.log(`Validaciones pendientes: ${this._pendingValidations.size}`);
    }
  }

  private processValidFile(file: File): void {
    if (this._internalFiles.length >= this.maxFiles) {
      this.toastQueue.addWarn(
        'Límite de archivos excedido',
        `No se pueden subir más de ${this.maxFiles} archivos.`,
        5000,
      );
      return;
    }

    const newFileItem: FileUploadItem = {
      file,
      name: file.name,
      progress: 0,
      isComplete: false,
      hasError: false,
      seleccionado: false,
    };

    this._internalFiles.push(newFileItem);
    this.filesSelected.emit([file]);
    this.filesChanged.emit(this._internalFiles);
  }

  /**
   * Procesa múltiples archivos válidos (para archivos que no requieren validación BD)
   */
  private processValidFiles(files: File[]): void {
    if (files.length === 0) return;

    if (this._internalFiles.length + files.length > this.maxFiles) {
      this.toastQueue.addWarn(
        'Límite de archivos excedido',
        `No se pueden subir más de ${this.maxFiles} archivos.`,
        5000,
      );
      return;
    }

    const newFileItems: FileUploadItem[] = files.map((file) => ({
      file,
      name: file.name,
      progress: 0,
      isComplete: false,
      hasError: false,
      seleccionado: false,
    }));

    this._internalFiles.push(...newFileItems);
    this.filesSelected.emit(files);
    this.filesChanged.emit(this._internalFiles);
  }
}
